import dayjs, {Dayjs} from 'dayjs';
import 'dayjs/locale/fr';
import {DAY_START_HOUR, SLOT_MINUTES, START_STEP_MINUTES} from '@/agenda/constants';

/**
 * Module-wide time policy: the agenda renders and edits in the
 * workstation's LOCAL time. The API exchanges ISO-8601 dates with offset,
 * so parsing is unambiguous. Do not mix in dateHelper's Europe/Paris
 * pinned formatters here: a card positioned in local time with a label
 * formatted in Paris time would contradict itself on non-metropolitan
 * or misconfigured workstations.
 */
export const parseAgendaDate = (date: string | Date): Dayjs => {
    return dayjs(date).locale('fr');
};

export const formatAgendaTime = (date: string | Date): string => {
    return parseAgendaDate(date).format('HH:mm');
};

/**
 * Minutes between a moment and the displayed day start, in wall-clock
 * terms: hour/minute of the moment plus 1440 per calendar day of distance.
 * Wall-clock (not timestamp) arithmetic keeps a 14:00 appointment on the
 * 14:00 row even on DST-change days; the day delta is rounded because a
 * DST-shortened day is 23 hours long.
 */
export const minutesFromDayStart = (date: Dayjs, dayStart: Dayjs): number => {
    const dayDelta = Math.round(date.startOf('day').diff(dayStart, 'hour') / 24);

    return dayDelta * 24 * 60 + date.hour() * 60 + date.minute();
};

/**
 * Slot to pre-fill a creation form with for the given day: the next round
 * slot when that day is today, the start of the grid otherwise. Callers must
 * pass the day they display, so an appointment is never silently booked on
 * another date than the one on screen.
 */
export const defaultSlotForDay = (day: Date, now: Date = new Date()): Date => {
    const target = parseAgendaDate(day);
    const current = parseAgendaDate(now);

    if (!target.isSame(current, 'day')) {
        return target.hour(DAY_START_HOUR).minute(0).second(0).millisecond(0).toDate();
    }

    const nextSlot = current.second(0).millisecond(0)
        .add(SLOT_MINUTES - (current.minute() % SLOT_MINUTES), 'minute');

    return nextSlot.toDate();
};

/**
 * The earliest start the server accepts: the beginning of the current step.
 */
export const earliestStart = (now: Date = new Date()): Dayjs => {
    const current = parseAgendaDate(now).second(0).millisecond(0);

    return current.minute(current.minute() - (current.minute() % START_STEP_MINUTES));
};

export const isPastStart = (start: string | Date, now: Date = new Date()): boolean => {
    return parseAgendaDate(start).isBefore(earliestStart(now));
};
